import { Grid } from '@mui/material';
import { Language, LanguageType } from '@nx-smartmonkey/shared/domain';
import { RoutalLoader as Loader, RoutalPalette } from '@nx-smartmonkey/ui';
import moment from 'moment';
import { useIntl } from 'react-intl';
import { Bar, BarChart, CartesianGrid, ResponsiveContainer, Tooltip, TooltipProps, XAxis, YAxis } from 'recharts';

import { useEffect } from 'react';
import { Payload } from 'recharts/types/component/DefaultTooltipContent';
import { EventTrackTypes } from '../../../constants/EventTracks';
import { DriversProjectUnits } from '../../../domain/driversProject/DriversProject';
import { useRetrieveActiveLanguage } from '../../../hooks/translations/useRetrieveActiveLanguage';
import { useCaptureEvents } from '../../../hooks/useCaptureEvents';
import { Text } from '../../../react-components/Text';
import { useRetrieveVehicle } from '../hooks/useRetrieveVehicle';
import { AnalyticsChartTooltip } from './AnalyticsChartTooltip';
import { useAnalyticsFilters } from './hooks/useAnalyticsFilters';
import { useRetrieveAnalyticsDistance } from './hooks/useRetrieveAnalyticsDistance';

interface AnalyticsDistanceChartTooltipProps extends TooltipProps<number, string> {
  locale: LanguageType;
  units?: DriversProjectUnits;
}

const AnalyticsDistanceChartTooltip = ({ active, payload, units, locale }: AnalyticsDistanceChartTooltipProps) => {
  const intl = useIntl();

  if (!active || (payload ?? []).length === 0) {
    return null;
  }

  const localeMoment = moment(payload![0].payload.date).locale(locale);
  const dateLabel = localeMoment.format(`L`);

  let label;

  if (units?.distance === `mi`) {
    label = intl.formatMessage({ id: `drivers.analytics.tab.distance_traveled.mi` });
  } else {
    label = intl.formatMessage({ id: `drivers.analytics.tab.distance_traveled.km` });
  }

  return (
    <AnalyticsChartTooltip
      label={dateLabel}
      data={payload!.map(({ color, name, value }: Payload<number, string>) => ({
        label,
        value: value ?? 0,
        color: color ?? `transparent`,
      }))}
    />
  );
};

export const AnalyticsDistance = () => {
  const { activeLanguage } = useRetrieveActiveLanguage();
  const { binType, startDate, endDate } = useAnalyticsFilters();
  const { data: vehicleDetails } = useRetrieveVehicle();
  const { capture } = useCaptureEvents();

  useEffect(() => {
    capture({
      type: EventTrackTypes.HomeAnalytics,
      payload: {
        [`Action`]: `Open`,
        [`Category`]: `KM`,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const locale = activeLanguage ?? Language.getDefaultLanguage().value;

  const {
    error,
    isLoading,
    data: distanceData,
  } = useRetrieveAnalyticsDistance({
    startDate,
    endDate,
    binType,
  });

  return (
    <Grid
      container
      direction="column"
      wrap="nowrap"
      style={{
        height: `100%`,
        maxHeight: `250px`,
        paddingTop: `10px`,
        paddingRight: `10px`,
      }}
    >
      {isLoading ? (
        <Grid
          container
          direction="column"
          wrap="nowrap"
          alignItems="center"
          justifyContent="center"
          style={{ height: `100%` }}
        >
          <Loader width={40} />
        </Grid>
      ) : (
        <>
          {error || !distanceData ? (
            <Grid
              container
              direction="column"
              wrap="nowrap"
              alignItems="center"
              justifyContent="center"
              style={{ height: `100%` }}
            >
              <Text intlId="drivers.analytics.retrieve_data_error" style={{ color: RoutalPalette.white }} />
            </Grid>
          ) : (
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={distanceData}
                barSize={20}
                margin={{
                  top: 0,
                  right: 0,
                  left: 0,
                  bottom: 0,
                }}
              >
                <CartesianGrid vertical={false} stroke={RoutalPalette.white} />
                <XAxis dataKey="dateLabel" fontSize="9px" stroke={RoutalPalette.white} axisLine={false} />
                <YAxis
                  width={25} // Aquest valor esta fixat perquè no es deixi un espai a l'esquerra. Si es canvia la mida del text, això s'haurà d'ajustar.
                  allowDecimals={false}
                  fontSize="9px"
                  stroke={RoutalPalette.white}
                  axisLine={false}
                  tickFormatter={(value: any, index: number) => {
                    if (value >= 1000) {
                      return `${Math.round(value / 1000)}k`;
                    }
                    return value;
                  }}
                />
                <Tooltip
                  content={(props: TooltipProps<number, string>) => (
                    <AnalyticsDistanceChartTooltip {...props} locale={locale} units={vehicleDetails?.project.units} />
                  )}
                />
                <Bar
                  barSize={20}
                  dataKey="value"
                  fill={RoutalPalette.primary20}
                  style={{
                    transform: `translate(0, -1px)`,
                  }}
                />
              </BarChart>
            </ResponsiveContainer>
          )}
        </>
      )}
    </Grid>
  );
};
